import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { useSiteAdmin } from '@/hooks/useSiteAdmin';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles } from 'lucide-react';
import { RealtorAdManager } from '@/components/admin/RealtorAdManager';
import { SiteAdManager } from '@/components/admin/SiteAdManager';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';

interface RealtorProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  logo_url: string | null;
  ad_image_url: string | null;
}

interface SiteAdCount {
  buyer: number;
  seller: number;
  market_value: number;
  mortgage: number;
  community_guide: number;
}

export default function AdsManage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const { isGlobalAdmin, selectedSiteId, selectedSite, loading: globalLoading, checked: globalChecked } = useGlobalAdmin();
  const { site, loading: siteLoading } = useSiteAdmin();

  const [loading, setLoading] = useState(true);
  const [realtorProfile, setRealtorProfile] = useState<RealtorProfile | null>(null);
  const [adCounts, setAdCounts] = useState<SiteAdCount>({ buyer: 0, seller: 0, market_value: 0, mortgage: 0, community_guide: 0 });
  const [generatingAll, setGeneratingAll] = useState(false);

  const effectiveSiteId = isGlobalAdmin ? selectedSiteId : site?.id;
  const effectiveOwnershipType = isGlobalAdmin ? selectedSite?.ownership_type : site?.ownership_type;
  const isCommunity = effectiveOwnershipType?.toLowerCase() === 'community';
  const [activeTab, setActiveTab] = useState('site');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  // Set default tab based on ownership type once data loads
  useEffect(() => {
    if (globalChecked && effectiveOwnershipType) {
      setActiveTab(isCommunity ? 'site' : 'realtor');
    }
  }, [globalChecked, effectiveOwnershipType, isCommunity]);

  useEffect(() => {
    if (effectiveSiteId && globalChecked) {
      fetchRealtorProfile();
      fetchAdCounts();
    }
  }, [effectiveSiteId, globalChecked]);

  const fetchRealtorProfile = async () => {
    if (!effectiveSiteId) return;

    try {
      // First try realtor_profiles
      const { data, error } = await supabase
        .from('realtor_profiles')
        .select('id, first_name, last_name, photo_url, logo_url, ad_image_url')
        .eq('site_id', effectiveSiteId)
        .maybeSingle();

      if (error) throw error;
      
      // If we have a profile but no name, try to get from site_admins
      if (data && !data.first_name && !data.last_name) {
        const { data: adminData } = await supabase
          .from('site_admins')
          .select('first_name, last_name, photo_url, logo_url')
          .eq('site_id', effectiveSiteId)
          .maybeSingle();
        
        if (adminData) {
          setRealtorProfile({
            ...data,
            first_name: adminData.first_name || data.first_name,
            last_name: adminData.last_name || data.last_name,
            photo_url: data.photo_url || adminData.photo_url,
            logo_url: data.logo_url || adminData.logo_url,
          });
          return;
        }
      }
      
      setRealtorProfile(data);
    } catch (error) {
      console.error('Error fetching realtor profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdCounts = async () => {
    if (!effectiveSiteId) return;

    const { data, error } = await supabase
      .from('site_ads')
      .select('ad_type')
      .eq('site_id', effectiveSiteId)
      .in('ad_type', ['buyer', 'seller', 'market_value', 'mortgage', 'community_guide']);

    if (!error && data) {
      const counts = { buyer: 0, seller: 0, market_value: 0, mortgage: 0, community_guide: 0 };
      data.forEach(ad => {
        if (ad.ad_type === 'buyer') counts.buyer++;
        if (ad.ad_type === 'seller') counts.seller++;
        if (ad.ad_type === 'market_value') counts.market_value++;
        if (ad.ad_type === 'mortgage') counts.mortgage++;
        if (ad.ad_type === 'community_guide') counts.community_guide++;
      });
      setAdCounts(counts);
    }
  };

  const handleGenerateAllMissing = async () => {
    if (!effectiveSiteId) return;

    setGeneratingAll(true);
    try {
      const missingBuyer = 3 - adCounts.buyer;
      const missingSeller = 3 - adCounts.seller;
      const missingMarketValue = 3 - adCounts.market_value;
      const missingMortgage = 3 - adCounts.mortgage;
      const missingCommunityGuide = 3 - adCounts.community_guide;
      const totalToGenerate = missingBuyer + missingSeller + missingMarketValue + missingMortgage + missingCommunityGuide;

      if (totalToGenerate === 0) {
        toast({ title: 'All ads already exist', description: 'You have 3 of each ad type.' });
        return;
      }

      toast({ 
        title: 'Generating ads...', 
        description: `Creating ${totalToGenerate} ads in parallel. This may take a minute.` 
      });

      // Build array of generation promises for parallel execution
      const generatePromises: Promise<boolean>[] = [];

      const generateAd = async (adType: 'buyer' | 'seller' | 'market_value' | 'mortgage' | 'community_guide'): Promise<boolean> => {
        try {
          const response = await supabase.functions.invoke('generate-site-ads', {
            body: { 
              siteId: effectiveSiteId, 
              adType,
              photoUrl: realtorProfile?.photo_url,
              logoUrl: realtorProfile?.logo_url,
            },
          });
          return !!response.data?.success;
        } catch {
          return false;
        }
      };

      // Queue all missing buyer ads
      for (let i = 0; i < missingBuyer; i++) {
        generatePromises.push(generateAd('buyer'));
      }
      // Queue all missing seller ads
      for (let i = 0; i < missingSeller; i++) {
        generatePromises.push(generateAd('seller'));
      }
      // Queue all missing market value report ads
      for (let i = 0; i < missingMarketValue; i++) {
        generatePromises.push(generateAd('market_value'));
      }
      // Queue all missing mortgage ads
      for (let i = 0; i < missingMortgage; i++) {
        generatePromises.push(generateAd('mortgage'));
      }
      // Queue all missing community guide ads
      for (let i = 0; i < missingCommunityGuide; i++) {
        generatePromises.push(generateAd('community_guide'));
      }

      // Execute all in parallel
      const results = await Promise.all(generatePromises);
      const generated = results.filter(Boolean).length;
      const failed = results.length - generated;

      await fetchAdCounts();

      if (generated > 0) {
        toast({ 
          title: 'Ads generated!', 
          description: `Successfully created ${generated} ads.${failed > 0 ? ` ${failed} failed.` : ''}`
        });
        // Reload the page to refresh the SiteAdManager components
        window.location.reload();
      } else {
        toast({ 
          title: 'Generation failed', 
          description: 'Could not generate any ads. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error generating ads:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to generate ads.',
        variant: 'destructive',
      });
    } finally {
      setGeneratingAll(false);
    }
  };

  const isLoading = authLoading || globalLoading || siteLoading || !globalChecked || loading;
  const missingAdsCount = (3 - adCounts.buyer) + (3 - adCounts.seller) + (3 - adCounts.market_value) + (3 - adCounts.mortgage) + (3 - adCounts.community_guide);

  if (isLoading) {
    return (
      <AdminLayout title="Ads">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  // Build realtor name with fallback to site name
  const realtorName = [realtorProfile?.first_name, realtorProfile?.last_name].filter(Boolean).join(' ') 
    || site?.name 
    || 'Real Estate Professional';

  return (
    <AdminLayout title="Ads">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold">Advertisements</h1>
          <p className="text-muted-foreground">
            Manage buyer and seller ads. Each type supports up to 3 images that rotate randomly.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            {!isCommunity && <TabsTrigger value="realtor">Realtor Ads</TabsTrigger>}
            <TabsTrigger value="site">Site Ads</TabsTrigger>
          </TabsList>

          <TabsContent value="realtor" className="mt-6 space-y-6">
            {realtorProfile ? (
              <Card>
                <CardHeader>
                  <CardTitle>Realtor Ads</CardTitle>
                  <CardDescription>
                    Buyer ads route to /buyers page. Seller ads route to /sellers page.
                    Click on an ad to toggle active status.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <RealtorAdManager
                    realtorProfileId={realtorProfile.id}
                    realtorName={realtorName}
                    photoUrl={realtorProfile.photo_url}
                    logoUrl={realtorProfile.logo_url}
                    adImageUrl={realtorProfile.ad_image_url}
                    onAdImageChange={(url) => {
                      setRealtorProfile(prev => prev ? { ...prev, ad_image_url: url } : null);
                    }}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">
                    Please set up your realtor profile first to manage realtor ads.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="site" className="mt-6 space-y-6">
            {effectiveSiteId && (
              <>
                {/* Generate All Button */}
                {missingAdsCount > 0 && (
                  <Card className="bg-primary/5 border-primary/20">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="font-medium">Missing {missingAdsCount} ads</p>
                          <p className="text-sm text-muted-foreground">
                            Generate all missing buyer and seller ads with AI
                          </p>
                        </div>
                        <Button 
                          onClick={handleGenerateAllMissing} 
                          disabled={generatingAll}
                          variant="default"
                        >
                          {generatingAll ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              Generate All Missing
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Buyer Ads (Community Guide) */}
                <Card>
                  <CardHeader>
                    <CardTitle>Community Guide Ads (Buyers)</CardTitle>
                    <CardDescription>
                      Displayed to attract home buyers. Routes to /buyers page.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SiteAdManager
                      siteId={effectiveSiteId}
                      adType="buyer"
                      photoUrl={realtorProfile?.photo_url}
                      logoUrl={realtorProfile?.logo_url}
                      onActiveImageChange={() => {}}
                    />
                  </CardContent>
                </Card>

                {/* Seller Ads */}
                <Card>
                  <CardHeader>
                    <CardTitle>Seller Ads</CardTitle>
                    <CardDescription>
                      Displayed to attract home sellers. Routes to /sellers page.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SiteAdManager
                      siteId={effectiveSiteId}
                      adType="seller"
                      photoUrl={realtorProfile?.photo_url}
                      logoUrl={realtorProfile?.logo_url}
                      onActiveImageChange={() => {}}
                    />
                  </CardContent>
                </Card>

                {/* Market Value Report Ads */}
                <Card>
                  <CardHeader>
                    <CardTitle>Market Value Report Ads</CardTitle>
                    <CardDescription>
                      Home valuation ads for sellers. Routes to /sellers page.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SiteAdManager
                      siteId={effectiveSiteId}
                      adType="market_value"
                      photoUrl={realtorProfile?.photo_url}
                      logoUrl={realtorProfile?.logo_url}
                      onActiveImageChange={() => {}}
                    />
                  </CardContent>
                </Card>

                {/* Mortgage Ads */}
                {/* Mortgage Ads */}
                <Card>
                  <CardHeader>
                    <CardTitle>Mortgage Info Ads</CardTitle>
                    <CardDescription>
                      Displayed on mortgage-related pages. Routes to /mortgages page.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SiteAdManager
                      siteId={effectiveSiteId}
                      adType="mortgage"
                      photoUrl={realtorProfile?.photo_url}
                      logoUrl={realtorProfile?.logo_url}
                      onActiveImageChange={() => {}}
                    />
                  </CardContent>
                </Card>

                {/* Community Guide Ads */}
                <Card>
                  <CardHeader>
                    <CardTitle>Community Guide Ads</CardTitle>
                    <CardDescription>
                      Promote downloadable community guides. Routes to /community-guide page.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SiteAdManager
                      siteId={effectiveSiteId}
                      adType="community_guide"
                      photoUrl={realtorProfile?.photo_url}
                      logoUrl={realtorProfile?.logo_url}
                      onActiveImageChange={() => {}}
                    />
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
